import { SDK } from "@axl-dev/school-sdk";
import { Spin } from "antd";
import { useEffect, useMemo, useState } from "react";

import { useLoc } from "@app/shared/lib/hooks";
import { generateBtnHoverCss, parseBtnHoverProps, BtnHoverProps } from "@app/widgets/shared/btnHoverCss";

export type ForumWidgetProps = BtnHoverProps & {
  sdk: SDK;
  cardBgColor: string;
  cardBorderColor: string;
  cardBorderRadius: string;
  cardPadding: string;
  titleFontSize: string;
  titleFontFamily: string;
  titleFontWeight: string;
  titleTextColor: string;
  titleTextAlign: string;
  showThreadDescription: boolean;
  showThreadCounts: boolean;
  itemBorderColor: string;
  itemPaddingV: string;
  itemTitleFontSize: string;
  itemTitleColor: string;
  itemDescFontSize: string;
  itemDescColor: string;
  itemMetaColor: string;
  itemMetaFontSize: string;
  showViewAllBtn: boolean;
  btnText: string;
  btnBgColor: string;
  btnTextColor: string;
  btnBorderRadius: string;
  btnFontSize: string;
  emptyText: string;
  emptyTextColor: string;
  emptyTextFontSize: string;
  title?: string;
  prefetchedForumData?: any;
};

export const parseForumProps = (container: HTMLElement): Omit<ForumWidgetProps, "sdk"> => {
  const attr = (name: string, fallback = "") => container.getAttribute(name) || fallback;
  return {
    cardBgColor: attr("data-card-bg-color", "#ffffff"),
    cardBorderColor: attr("data-card-border-color", "#f0f0f0"),
    cardBorderRadius: attr("data-card-border-radius", "8px"),
    cardPadding: attr("data-card-padding", "16px"),
    titleFontSize: attr("data-title-font-size", "20px"),
    titleFontFamily: attr("data-title-font-family"),
    titleFontWeight: attr("data-title-font-weight", "600"),
    titleTextColor: attr("data-title-text-color"),
    titleTextAlign: attr("data-title-text-align", "left"),
    showThreadDescription: attr("data-show-thread-description", "true") === "true",
    showThreadCounts: attr("data-show-thread-counts", "true") === "true",
    itemBorderColor: attr("data-item-border-color", "#f0f0f0"),
    itemPaddingV: attr("data-item-padding-v", "12px"),
    itemTitleFontSize: attr("data-item-title-font-size", "15px"),
    itemTitleColor: attr("data-item-title-color", "#333333"),
    itemDescFontSize: attr("data-item-desc-font-size", "13px"),
    itemDescColor: attr("data-item-desc-color", "#888888"),
    itemMetaColor: attr("data-item-meta-color", "#999999"),
    itemMetaFontSize: attr("data-item-meta-font-size", "12px"),
    showViewAllBtn: attr("data-show-view-all-btn", "true") === "true",
    btnText: attr("data-btn-text"),
    btnBgColor: attr("data-btn-bg-color", "#1890ff"),
    btnTextColor: attr("data-btn-text-color", "#ffffff"),
    btnBorderRadius: attr("data-btn-border-radius", "5px"),
    btnFontSize: attr("data-btn-font-size", "14px"),
    emptyText: attr("data-empty-text"),
    emptyTextColor: attr("data-empty-text-color", "#aaaaaa"),
    emptyTextFontSize: attr("data-empty-text-font-size", "14px"),
    title: attr("data-title") || undefined,
    ...parseBtnHoverProps(container),
  };
};

export const ForumWidget: React.FC<ForumWidgetProps> = (props) => {
  const loc = useLoc();
  const scopeId = useMemo(() => `ez-fw-${Math.random().toString(36).slice(2, 8)}`, []);
  const hoverCss = useMemo(() => generateBtnHoverCss(`.${scopeId}`, "button, a", props), [scopeId, props.btnHoverEffect, props.btnHoverBgColor, props.btnHoverTextColor, props.btnHoverShadow, props.btnHoverScale, props.btnHoverTransitionDuration]);
  const [data, setData] = useState<any>(props.prefetchedForumData || null);
  const [loading, setLoading] = useState(!props.prefetchedForumData);

  useEffect(() => {
    if (props.prefetchedForumData) { setData(props.prefetchedForumData); setLoading(false); return; }
    if (!props.sdk.forum?.thread) { setLoading(false); return; }
    let cancelled = false;
    props.sdk.forum.thread.fetch().then((res: any) => {
      if (!cancelled && res.success && res.body) setData(res.body);
    }).catch((e: unknown) => { console.error("[ForumWidget] fetch error:", e); }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [props.sdk, props.prefetchedForumData]);

  const cardStyle: React.CSSProperties = {
    border: `1px solid ${props.cardBorderColor || "#f0f0f0"}`,
    borderRadius: props.cardBorderRadius || "8px",
    backgroundColor: props.cardBgColor || "#fff",
    padding: props.cardPadding || "16px",
  };

  if (loading) return <div style={cardStyle}><Spin /></div>;

  const threads: any[] = data?.threads || [];
  const emptyLabel = props.emptyText || loc.word("Forum.empty", { default: "No forum content yet" });

  return (
    <div className={scopeId}>
      <style>{hoverCss}</style>
      {props.title && (
        <div style={{
          fontSize: props.titleFontSize || "20px",
          fontWeight: (props.titleFontWeight as any) || 600,
          fontFamily: props.titleFontFamily || undefined,
          color: props.titleTextColor || undefined,
          textAlign: (props.titleTextAlign as any) || "left",
          marginBottom: "12px",
        }}>{props.title}</div>
      )}
      <div style={cardStyle}>
        {threads.length === 0 ? (
          <div style={{ textAlign: "center", padding: "20px", color: props.emptyTextColor || "#aaa", fontSize: props.emptyTextFontSize || "14px" }}>{emptyLabel}</div>
        ) : (
          threads.map((item: any, i: number) => (
            <div key={item.id || i} style={{ borderBottom: i < threads.length - 1 ? `1px solid ${props.itemBorderColor || "#f0f0f0"}` : undefined, padding: `${props.itemPaddingV || "12px"} 0` }}>
              <a href={`/forum/${item.id}`}
                style={{ fontSize: props.itemTitleFontSize || "15px", fontWeight: 500, color: props.itemTitleColor || "#333", textDecoration: "none", display: "block", marginBottom: "4px" }}>
                {item.title}
              </a>
              {props.showThreadDescription && item.description && (
                <div style={{ fontSize: props.itemDescFontSize || "13px", color: props.itemDescColor || "#888", marginBottom: "4px" }}>{item.description}</div>
              )}
              {props.showThreadCounts && (
                <div style={{ fontSize: props.itemMetaFontSize || "12px", color: props.itemMetaColor || "#999" }}>
                  {item.topicCount || 0} {loc.word("Forum.topics", { default: "topics" })} · {item.commentCount || 0} {loc.word("Forum.comments", { default: "comments" })}
                </div>
              )}
            </div>
          ))
        )}
        {props.showViewAllBtn && threads.length > 0 && (
          <div style={{ textAlign: "center", marginTop: "12px" }}>
            <a href="/forum" style={{
              display: "inline-block", padding: "6px 20px", textDecoration: "none",
              borderRadius: props.btnBorderRadius || "5px", fontSize: props.btnFontSize || "14px",
              backgroundColor: props.btnBgColor || "#1890ff", color: props.btnTextColor || "#fff",
            }}>
              {props.btnText || loc.word("Forum.viewAll", { default: "View forum" })}
            </a>
          </div>
        )}
      </div>
    </div>
  );
};
